import { useEffect, useState } from 'react';
import { trackView } from '../utils/analytics';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, BedDouble, Car, Compass, ExternalLink, Loader2, MapPin, ShieldCheck, Star } from 'lucide-react';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { useCart } from '../utils/cart';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { MiniMap } from '../components/MiniMap';
import { PriceOnRequest } from '../components/PriceOnRequest';
import { EstimatePrice } from '../components/EstimatePrice';
import { AccommodationCard, AccommodationRating, AccommodationTypeBadge } from '../components/AccommodationCard';
import { StayGallery } from '../components/StayGallery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ title, icon: Icon, children, testid, id }) => (
  <section id={id} data-testid={testid} className="rounded-2xl border border-eco-ink/10 bg-white p-6">
    <h2 className="flex items-center gap-2 text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{Icon && <Icon size={17} className="text-eco-gold" />} {title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

export default function AccommodationDetailPage() {
  const { slug } = useParams();
  const { add: addToCart, includes: cartIncludes } = useCart();
  const { t, lang } = useLang();
  const locale = { pt: 'pt-PT', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' }[lang] || 'pt-PT';
  const [acc, setAcc] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setAcc(null); setNotFound(false);
    fetch(`${API}/accommodations/${slug}`)
      .then((r) => { if (!r.ok) throw new Error('nf'); return r.json(); })
      .then(setAcc)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (acc?.slug) trackView('accommodation', acc.slug);
  }, [acc?.slug]);

  useEffect(() => {
    if (!acc) return;
    fetch(`${API}/accommodations?island=${encodeURIComponent(acc.island)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRelated((Array.isArray(d) ? d : []).filter((x) => x.slug !== acc.slug).slice(0, 3)))
      .catch(() => {});
  }, [acc]);

  useSEO(acc ? `${acc.name} | Eco Collection` : 'Alojamento | Eco Collection', acc?.description || 'Alojamento Eco Collection.');

  if (notFound) {
    return (
      <div data-testid="accommodation-not-found">
        <PageHeader kicker="Eco Collection" title={t('acc.notfound.t')} sub={t('acc.notfound.s')} />
        <div className="bg-eco-cream py-14 text-center"><Link to="/accommodations" className="eco-btn-primary inline-flex" data-testid="accommodation-back-notfound"><ArrowLeft size={15} /> {t('acc.back')}</Link></div>
      </div>
    );
  }
  if (!acc) return <div className="grid min-h-[50vh] place-items-center" data-testid="accommodation-loading"><Loader2 className="animate-spin text-eco-green" size={28} /></div>;

  return (
    <div data-testid="accommodation-detail-page">
      <PageHeader kicker={`${acc.island}${acc.country === 'CV' ? ' · Cabo Verde' : ''}`} title={acc.name} sub={acc.address || ''} />
      <section className="bg-eco-cream py-10 md:py-14">
        <div className="eco-container max-w-5xl space-y-5">
          <Link to="/accommodations" data-testid="accommodation-back" className="eco-btn-text inline-flex"><ArrowLeft size={15} /> {t('acc.back')}</Link>

          <Reveal><StayGallery stay={acc} /></Reveal>

          <Reveal>
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-eco-ink/10 bg-white p-5" data-testid="accommodation-summary">
              <AccommodationRating rating={acc.rating} isNew={acc.is_new} t={t} testid="accommodation-rating" />
              <AccommodationTypeBadge acc={acc} t={t} testid="accommodation-ptype" />
              {acc.property_type && <span className="rounded-full bg-eco-ink/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide">{t(`stay.type.${acc.property_type}`)}</span>}
              {acc.star_rating && <span aria-label={`${acc.star_rating} ${t('stay.officialStars')}`} className="text-sm font-semibold text-eco-gold">{'★'.repeat(acc.star_rating)}</span>}
              <span className="eco-meta ml-auto flex items-center gap-1.5"><MapPin size={13} /> {acc.island}</span>
              {acc.price_from != null ? (
                <p data-testid="accommodation-price" className="text-sm text-eco-ink/55">{t('acc.from')} {(acc.currency || 'EUR') === 'EUR' ? <EstimatePrice testid="accommodation-price-value" amountEur={acc.price_from} /> : <strong className="eco-number text-xl text-eco-green">{acc.price_from.toFixed(2)} {acc.currency}</strong>}</p>
              ) : (
                <PriceOnRequest compact testid="accommodation-price" />
              )}
              <button
                type="button"
                data-testid="accommodation-add-trip"
                onClick={() => {
                  const result = addToCart({ type: 'accommodation', slug: acc.slug, name: acc.name, destination: acc.island, priceEur: (acc.currency || 'EUR') === 'EUR' ? acc.price_from : null });
                  if (!result.added) toast(t('cart.duplicate'), { description: acc.name });
                  else toast.success(t('cta.addtrip'), { description: acc.name });
                }}
                className="eco-btn-text min-h-11"
              >
                {cartIncludes('accommodation', acc.slug) ? '✓ ' : ''}{t('cta.addtrip')}
              </button>
            </div>
          </Reveal>

          {(acc.overview || acc.description || acc.short_description) && (
            <Reveal><Section title={t('acc.about')} icon={BedDouble} testid="accommodation-description">
              {acc.short_description && <p className="font-semibold text-eco-ink">{acc.short_description}</p>}
              <p className="eco-body mt-2 whitespace-pre-line text-eco-ink/70">{acc.overview || acc.description}</p>
              {acc.highlights?.length > 0 && <div className="mt-5"><p className="eco-field-label">{t('stay.highlights')}</p><ul className="mt-2 grid gap-2 sm:grid-cols-2">{acc.highlights.map((item) => <li key={item} className="flex gap-2 text-sm text-eco-ink/70"><BadgeCheck size={15} className="mt-0.5 shrink-0 text-eco-green" />{item}</li>)}</ul></div>}
            </Section></Reveal>
          )}

          {acc.room_types?.length > 0 && (
            <Reveal><Section title={t('acc.rooms')} icon={BedDouble} testid="accommodation-rooms">
              <div className="grid gap-3 sm:grid-cols-2">
                {acc.room_types.map((r, i) => (
                  <div key={i} data-testid={`accommodation-room-${i}`} className="rounded-xl bg-eco-cream p-4">
                    <p className="text-sm font-semibold text-eco-ink">{r.name}</p>
                    {r.images?.[0] && <img src={r.images[0]} alt={r.name} className="mb-3 aspect-[16/9] w-full rounded-lg object-cover" />}
                    {r.description && <p className="mt-1 text-sm text-eco-ink/65">{r.description}</p>}
                    <p className="eco-meta mt-1">{[r.capacity && `${r.capacity} ${t('acc.guests')}`, r.capacity_adults != null && `${r.capacity_adults} ${t('form.adults')}`, r.bed_configuration || r.bed_type, r.size_m2 && `${r.size_m2} m²`, r.view, r.note].filter(Boolean).join(' · ')}</p>
                    <div className="mt-3"><PriceOnRequest compact /></div>
                  </div>
                ))}
              </div>
            </Section></Reveal>
          )}

          {(acc.facilities?.length > 0 || acc.amenities?.length > 0) && (
            <Reveal><Section title={t('acc.amenities')} testid="accommodation-amenities">
              <div className="flex flex-wrap gap-2">
                {(acc.facilities || []).map((a) => <span key={a} className="rounded-full bg-eco-cream px-3.5 py-1.5 text-xs font-medium text-eco-ink/70">{t(`stay.facility.${a}`)}</span>)}
                {(acc.amenities || []).map((a) => <span key={`legacy-${a}`} className="rounded-full bg-eco-cream px-3.5 py-1.5 text-xs font-medium text-eco-ink/70">{a}</span>)}
              </div>
            </Section></Reveal>
          )}

          {acc.good_for?.length > 0 && <Reveal><Section title={t('stay.goodFor')} testid="accommodation-good-for"><div className="flex flex-wrap gap-2">{acc.good_for.map((item) => <span key={item} className="rounded-full bg-eco-green/10 px-3.5 py-1.5 text-xs font-medium text-eco-green">{t(`stay.goodFor.${item}`)}</span>)}</div></Section></Reveal>}

          {(acc.location_details || acc.address || acc.nearby?.length > 0 || (typeof acc.map_lat === 'number' && typeof acc.map_lng === 'number')) && (
            <Reveal><Section title={t('acc.location')} icon={MapPin} testid="accommodation-location">
              {(acc.location_details || acc.address) && <p className="mb-4 text-sm text-eco-ink/70">{acc.location_details || acc.address}</p>}
              {typeof acc.map_lat === 'number' && typeof acc.map_lng === 'number' && <MiniMap island={acc.island} items={[{ lat: acc.map_lat, lng: acc.map_lng, name: acc.name }]} view={{ center: [acc.map_lat, acc.map_lng], zoom: 12 }} />}
              {acc.nearby?.length > 0 && <ul className="mt-4 grid gap-2 sm:grid-cols-2">{acc.nearby.map((place) => <li key={place.name} className="text-sm text-eco-ink/70"><strong>{place.name}</strong>{place.distance != null && ` · ${place.distance} ${place.distance_unit}`}</li>)}</ul>}
            </Section></Reveal>
          )}

          <Reveal>
            <div className="grid gap-4 sm:grid-cols-2">
              <Link to={`/experiences?island=${encodeURIComponent(acc.island)}`} data-testid="accommodation-nearby-experiences" className="eco-card flex items-center gap-3 p-5 transition-shadow hover:shadow-md">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-eco-green text-eco-gold"><Compass size={17} /></span>
                <span><span className="block text-sm font-semibold text-eco-ink">{t('acc.nearby')}</span><span className="eco-meta">{acc.island}</span></span>
                <ArrowRight size={15} className="ml-auto text-eco-ink/30" />
              </Link>
              <Link to={`/transfers?destination=${encodeURIComponent(acc.island)}`} data-testid="accommodation-transfer-options" className="eco-card flex items-center gap-3 p-5 transition-shadow hover:shadow-md">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-eco-green text-eco-gold"><Car size={17} /></span>
                <span><span className="block text-sm font-semibold text-eco-ink">{t('acc.transfers')}</span><span className="eco-meta">{acc.island}</span></span>
                <ArrowRight size={15} className="ml-auto text-eco-ink/30" />
              </Link>
            </div>
          </Reveal>

          {acc.reviews?.length > 0 ? (
            <Reveal><Section title={`${t('acc.reviews.t')} · ${acc.rating?.avg}/5 (${acc.rating?.count})`} icon={Star} testid="accommodation-reviews">
              <ul className="space-y-4">
                {acc.reviews.map((r, i) => (
                  <li key={i} data-testid={`accommodation-review-${i}`} className="rounded-xl bg-eco-cream p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-eco-ink">{r.traveler_name}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-eco-gold"><Star size={12} className="fill-eco-gold" /> {r.rating}/5</span>
                      {r.verified_booking && <span className="inline-flex items-center gap-1 rounded-full bg-eco-green/10 px-2 py-0.5 text-[10px] font-bold text-eco-green"><ShieldCheck size={10} /> {t('acc.verified.booking')}</span>}
                      <span className="ml-auto text-xs text-eco-ink/40">{r.created_at ? new Date(r.created_at).toLocaleDateString(locale) : ''}</span>
                    </div>
                    {r.text && <p className="mt-2 text-sm text-eco-ink/70">{r.text}</p>}
                  </li>
                ))}
              </ul>
            </Section></Reveal>
          ) : <Reveal><Section title={t('acc.reviews.t')} icon={Star} testid="accommodation-reviews-empty"><p className="text-sm text-eco-ink/55">{t('stay.reviews.empty')}</p></Section></Reveal>}

          {acc.policies && (
            <Reveal><Section title={t('acc.policies')} icon={ShieldCheck} testid="accommodation-policies">
              <p className="text-sm whitespace-pre-line text-eco-ink/70">{acc.policies}</p>
            </Section></Reveal>
          )}

          {(acc.check_in || acc.check_out || Object.values(acc.structured_policies || {}).some(Boolean)) && <Reveal><Section title={t('acc.policies')} icon={ShieldCheck} testid="accommodation-structured-policies"><dl className="grid gap-3 sm:grid-cols-2">{acc.check_in && <div><dt className="eco-field-label">Check-in</dt><dd>{acc.check_in}</dd></div>}{acc.check_out && <div><dt className="eco-field-label">Check-out</dt><dd>{acc.check_out}</dd></div>}{Object.entries(acc.structured_policies || {}).filter(([, value]) => value).map(([key, value]) => <div key={key}><dt className="eco-field-label">{t(`stay.policy.${key}`)}</dt><dd className="text-sm text-eco-ink/70">{value}</dd></div>)}</dl></Section></Reveal>}

          {acc.sustainability?.length > 0 && <Reveal><Section title={t('stay.sustainability')} icon={BadgeCheck} testid="accommodation-sustainability"><ul className="grid gap-2 sm:grid-cols-2">{acc.sustainability.map((item) => <li key={item} className="flex gap-2 text-sm text-eco-ink/70"><BadgeCheck size={15} className="text-eco-green" />{t(`stay.sustainability.${item}`)}</li>)}</ul></Section></Reveal>}

          <Reveal>
            <div id="request" className="scroll-mt-24">
              {acc.booking_method === 'affiliate' && acc.affiliate_url ? (
                <div data-testid="accommodation-affiliate-cta" className="rounded-2xl border border-eco-ink/10 bg-white p-6 text-center">
                  <p className="text-sm text-eco-ink/60">{t('acc.affiliate.s')}</p>
                  <a href={acc.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="accommodation-affiliate-link" className="eco-btn-primary mt-4 inline-flex">{t('acc.affiliate.cta')} <ExternalLink size={14} /></a>
                  <p className="mt-3 text-xs text-eco-ink/40">{t('acc.affiliate.note')}</p>
                </div>
              ) : acc.booking_method === 'direct_contact' && acc.website ? (
                <div data-testid="accommodation-direct-cta" className="rounded-2xl border border-eco-ink/10 bg-white p-6 text-center">
                  <p className="text-sm text-eco-ink/60">{t('acc.direct.s')}</p>
                  <a href={acc.website} target="_blank" rel="noopener noreferrer" data-testid="accommodation-website-link" className="eco-btn-primary mt-4 inline-flex">{t('acc.direct.cta')} <ExternalLink size={14} /></a>
                </div>
              ) : <div className="rounded-2xl border border-eco-ink/10 bg-white p-6 text-center"><h2 className="text-xl font-semibold">{t('stay.request.title')}</h2><p className="mt-2 text-sm text-eco-ink/60">{t('stay.request.note')}</p><Link to={`/request-stay?stay=${encodeURIComponent(acc.slug)}`} className="eco-btn-primary mt-5 inline-flex">{t('stay.request.cta')} <ArrowRight size={15} /></Link></div>}
            </div>
          </Reveal>

          {related.length > 0 && (
            <Reveal><Section title={t('acc.related')} testid="accommodation-related">
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => <AccommodationCard key={r.id} acc={r} />)}
              </div>
            </Section></Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
